<template>
  <v-row no-gutters class="pt-5">
    <v-col v-if="isDataFetched" class="pr-2">
      <card-title-row translation-path="sidebar.customReports.title" class="pb-5" :has-buttons="false">
        <template #subtitle>
          <italic-subheader
            style="font-size: 12px !important;"
            class="text-left"
            translation-path="sidebar.customReports.subtitle"
          />
        </template>
      </card-title-row>
      <sidebar-row class="pb-5">
        <v-row>
          <v-col>
            <v-spacer></v-spacer>
          </v-col>
          <v-col style="max-width: 200px" class="mb-2">
            <data-input v-model="searchInput" v-bind="searchTypeInput" />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col v-if="filteredReports && filteredReports.length > 0" cols="12" class="elevation-3">
            <draggable-list
              :value.sync="filteredReports"
              disable-group-adding
              fill-width
              hideable-groups
              allow-elements-on-main-level
              child-items-key="reports"
              item-title-key="name"
              disable-draggable
            >
              <template #[`item.prepend-title`]="item">
                <div class="draggable-checkbox">
                  <v-checkbox
                    hide-details
                    v-bind="{ ...getCheckboxStatus(item.item) }"
                    @click="$event => $event.stopPropagation()"
                    @change="changeCheckbox($event, item.item)"
                    :ripple="false"
                  ></v-checkbox>
                </div>
              </template>
            </draggable-list>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row>
        <div class="text-left d-flex" style="gap: 0.2rem">
          <span class="text-body-2 font-weight-medium">{{ $i18n.t('sidebar.customReports.spatialBoundary') }}</span>
          <span class="text-caption font-italic font-weight-thin">{{ $i18n.t('sidebar.customReports.optional') }}</span>
        </div>
      </sidebar-row>
      <sidebar-row>
        <v-radio-group v-model="reportType" @change="changeType" row>
          <v-radio value="intersection">
            <template #label>
              <div class="label-text">
                {{ $i18n.t('sidebar.customReports.layerObjects') }}
              </div>
            </template>
          </v-radio>
          <v-radio value="extent">
            <template #label>
              <div class="label-text">
                {{ $i18n.t('sidebar.customReports.drawnZone') }}
              </div>
            </template>
          </v-radio>
        </v-radio-group>
      </sidebar-row>
      <sidebar-row v-if="reportType === 'intersection'">
        <sidebar-form
          v-model="payload.intersection"
          :is-valid.sync="isLayerFormValid"
          :inputs="inputs"
          :hasIconColumn="false"
        >
          <template v-for="input in inputs" #[input.name]="data">
            <data-input
              v-bind="data"
              v-on="data.listeners"
              v-model="payload.intersection[data.name]"
              :key="input.name"
            />
          </template>
        </sidebar-form>
      </sidebar-row>
      <sidebar-row v-else>
        <v-row no-gutters style="gap: 1rem">
          <v-col cols="auto">
            <data-input ref="geometry-input" v-model="payload.extent" v-bind="geometryInput" />
          </v-col>
          <v-col v-if="!!payload.extent" cols="auto">
            <base-button @click="clearGeometry" cancel translation-path="sidebar.customReports.clearArea"></base-button>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row>
        <v-checkbox hide-details v-model="payload.send_email">
          <template #label>
            <div class="label-text">
              {{ $i18n.t('sidebar.customReports.sendEmail') }}
            </div>
          </template>
        </v-checkbox>
      </sidebar-row>
      <v-menu v-model="isGenerateReportMenuOpened" absolute attach="#stickyButton-generateReport" top>
        <v-list>
          <v-list-item
            link
            v-for="button in generateReportMenuButtons"
            :key="button.value"
            @click="generateReport(button.value)"
          >
            <v-list-item-title>{{ button.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-col>
    <v-col v-else>
      <main-loader class="pt-5" />
    </v-col>
  </v-row>
</template>

<script>
import SidebarRow from '@/components/SidebarRow';
import CardTitleRow from '@/components/CardTitleRow';
import MainLoader from '@/components/MainLoader';
import ItalicSubheader from '@/components/ItalicSubheader';
import SidebarForm from '@/components/SidebarForm';
import DataInput from '@/components/DataInput';
import DraggableList from '@/components/DraggableList';
import { get, call, sync } from 'vuex-pathify';

export default {
  name: 'CustomReports',
  components: {
    SidebarRow,
    CardTitleRow,
    MainLoader,
    ItalicSubheader,
    SidebarForm,
    DataInput,
    DraggableList,
  },
  data: () => ({
    isFetched: true,
    objectsLoading: false,
    isGenerateReportMenuOpened: false,
    currentLayerFeatures: [],
    isLayerFormValid: false,
    selectedReports: {},
    searchInput: '',
    filteredReports: [],
    reportsTree: [],
    reportType: 'intersection',
    isAtLeastOneReportSelected: false,
    payload: {
      intersection: {},
      extent: null,
      send_email: false,
    },
  }),
  watch: {
    async isDataFetched(nV) {
      if (!nV) return;
      if (this.isFetched) this.isStickyButtonsVisible = true;
      const intersectionData = JSON.parse(localStorage.getItem('custom-reports-intersection-data') || '{}');
      if (intersectionData.layer_id) this.payload.intersection.layer_id = intersectionData.layer_id;
      if (intersectionData.attribute_name) {
        this.payload.intersection.attribute_name = intersectionData.attribute_name;
        await this.handleChangeDescId(intersectionData.attribute_name);
        if (intersectionData.values) this.payload.intersection.values = intersectionData.values;
        this.$forceUpdate();
      }
    },
    searchInput(nV) {
      this.filteredReports = this.getFilteredItems(nV);
    },
    selectedReports: {
      deep: true,
      handler(value) {
        this.isAtLeastOneReportSelected = Object.values(value).flat().length > 0;
      },
    },
    isFormValid(nV) {
      this.isStickyButtonsValid = nV;
    },
  },
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    reports: get('customReports/reports'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    isDataFetched() {
      return this.isFetched && this.mapPanelDataFetched;
    },
    isFormValid() {
      return this.isAtLeastOneReportSelected && (this.reportType === 'intersection' ? this.isLayerFormValid : true);
    },
    generateReportMenuButtons() {
      return [
        {
          value: 'pdf',
          title: this.$i18n.t('sidebar.customReports.generatePdf'),
        },
        {
          value: 'xlsx',
          title: this.$i18n.t('sidebar.customReports.generateXlsx'),
        },
      ];
    },
    searchTypeInput() {
      return {
        dataType: { name: 'text' },
        prependInnerIcon: 'mdi-magnify',
        placeholder: this.$i18n.t('default.find'),
        hideDetails: true,
        clearable: true,
      };
    },
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers).filter(layer => layer.geometry_type === 'polygon');
    },
    currentLayerAttributes() {
      return this.payload.intersection.layer_id
        ? this.$_flattenStructure(this.layers[this.payload.intersection.layer_id]?.form_schema.elements || [])
        : [];
    },
    geometryInput() {
      return {
        name: 'extentGeometry',
        dataType: { name: 'geometry' },
        hasNameColumn: false,
        cols: '12',
        class: 'pb-4',
        disabled: !!this.payload.extent,
        buttonProps: {
          translationPath: 'sidebar.customReports.drawArea',
        },
        geometryType: 'Polygon',
        rules: [v => !!v || this.$i18n.t('rules.required')],
      };
    },
    inputs() {
      return [
        {
          name: 'layer_id',
          cols: '7',
          nameCols: '5',
          dataType: { name: 'select' },
          items: this.layersArray,
          itemText: 'name',
          itemValue: 'id',
          clearable: true,
          listeners: {
            change: () => {
              this.payload.intersection.attribute_name = this.payload.intersection.values = null;
            },
          },
          translationPath: 'sidebar.customReports.layer',
        },
        {
          name: 'attribute_name',
          cols: '7',
          nameCols: '5',
          dataType: { name: 'select' },
          itemText: 'label',
          itemValue: 'attribute',
          listeners: {
            change: this.handleChangeDescId,
          },
          disabled: !this.payload.intersection.layer_id,
          items: this.currentLayerAttributes,
          translationPath: 'sidebar.customReports.idObjectsAttribute',
          rules: this.payload.intersection.layer_id ? [v => !!v || this.$i18n.t('rules.required')] : [],
        },
        {
          name: 'values',
          cols: '7',
          nameCols: '5',
          loading: this.objectsLoading,
          disabled: this.objectsLoading || !this.payload.intersection.attribute_name,
          dataType: { name: 'select' },
          multiple: true,
          autoConvertSelect: false,
          chips: true,
          menuProps: {
            maxWidth: 300,
          },
          items: this.currentLayerFeatures,
          itemText: 'name',
          translationPath: 'sidebar.customReports.objects',
          rules: this.payload.intersection.layer_id
            ? [v => (!!v && v.length > 0) || this.$i18n.t('rules.required')]
            : [],
        },
      ];
    },
  },
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    generateCustomReport: call('customReports/generateCustomReport'),
    ...call('customReports/getCustomReport*'),
    getFilteredItems(text) {
      let reportsCopy = JSON.parse(JSON.stringify(this.reportsTree));
      if (!text) return reportsCopy;
      const queryText = text.toLowerCase();
      const filteredData = reportsCopy.filter(group => {
        group.reports = group.reports.filter(report => report.name.toLowerCase().includes(queryText));
        return group.reports.length > 0;
      });
      return filteredData;
    },
    changeType() {
      this.clearGeometry();
      this.payload.intersection = {};
    },
    getCheckboxStatus(item) {
      const isGroup = !!item.reports;
      if (isGroup) {
        const reportsGroup = this.reports.find(report => report.group_id === item.group_id);
        const hasAnyReport = this.selectedReports[reportsGroup.group_id]?.length > 0;
        return {
          indeterminate: hasAnyReport && reportsGroup?.reports.length > this.selectedReports[item.group_id]?.length,
          inputValue: hasAnyReport,
        };
      } else {
        return {
          inputValue: this.selectedReports[item.group_id]?.includes(item.id),
        };
      }
    },
    changeCheckbox(e, item) {
      const isGroup = !!item.reports;
      if (isGroup) {
        const reportsGroup = this.reports.find(report => report.group_id === item.group_id);
        this.$set(this.selectedReports, item.group_id, e ? reportsGroup?.reports.map(report => report.id) : []);
      } else {
        if (!this.selectedReports[item.group_id]) this.$set(this.selectedReports, item.group_id, []);
        this.selectedReports[item.group_id]?.includes(item.id)
          ? this.$set(
              this.selectedReports,
              item.group_id,
              this.selectedReports[item.group_id].filter(report => report !== item.id)
            )
          : this.selectedReports[item.group_id].push(item.id);
      }
      this.$forceUpdate();
    },
    async handleChangeDescId(descId) {
      this.objectsLoading = true;
      this.payload.intersection.values = null;
      try {
        const r = await this.getLayerFeatures({
          layer_id: this.payload.intersection.layer_id,
          with_geometry: false,
        });
        this.currentLayerFeatures = [
          ...new Set(
            r.data.data.features.features
              .filter(ft => !!ft.properties[descId])
              .map(ft => {
                return {
                  id: ft.id,
                  name: ft.properties[descId],
                };
              })
          ),
        ];
      } finally {
        this.objectsLoading = false;
      }
    },
    clearGeometry() {
      this.payload.extent = null;
    },
    generateReportHandler() {
      this.isGenerateReportMenuOpened = true;
    },
    getPayload(type) {
      let payloadCopy = JSON.parse(JSON.stringify(this.payload));
      const isIntersection = this.reportType === 'intersection';
      if (isIntersection ? !payloadCopy[this.reportType].layer_id : !payloadCopy[this.reportType])
        delete payloadCopy[this.reportType];
      this.reportType === 'intersection' ? delete payloadCopy.extent : delete payloadCopy.intersection;
      if (payloadCopy.extent) {
        payloadCopy.extent = {
          type: 'FeatureCollection',
          crs: payloadCopy.extent.crs,
          features: [
            {
              type: 'Feature',
              geometry: payloadCopy.extent,
            },
          ],
        };
      }
      payloadCopy.report_format = type;
      return payloadCopy;
    },
    async generateReport(type) {
      this.isStickyButtonsLoading = true;
      try {
        if (this.payload.intersection.layer_id)
          localStorage.setItem('custom-reports-intersection-data', JSON.stringify(this.payload.intersection));
        await this.generateCustomReport({
          reportIds: Object.values(this.selectedReports)
            .flat()
            .join(','),
          payload: this.getPayload(type),
        });
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
    async init() {
      this.stickyButtons = [
        {
          action: 'generateReport',
          translationPath: 'sidebar.customReports.generateReport',
          id: 'stickyButton-generateReport',
        },
      ];
      this.$root.$off('generateReport');
      this.$root.$on('generateReport', this.generateReportHandler);
      this.isStickyButtonsLoading = false;
      this.isStickyButtonsValid = false;
      this.isStickyButtonsVisible = false;
      await Promise.all([this.getCustomReportsGroups(), this.getCustomReports()]);
      if (this.mapPanelDataFetched) this.isStickyButtonsVisible = true;
      const hiddenGroups = JSON.parse(localStorage.getItem('custom-reports-hidden-groups') || '{}');
      this.reportsTree = this.reports.map(group => {
        return {
          ...group,
          id: group.group_id,
          groupName: 'custom-reports',
          isHidden: hiddenGroups[group.group_id] || false,
          reports: group.reports.map(report => {
            return {
              ...report,
              tooltip: report.name,
              group_id: group.group_id,
            };
          }),
          name: group.group_name,
        };
      });
      this.filteredReports = JSON.parse(JSON.stringify(this.reportsTree));
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>

<style lang="scss" scoped>
.draggable-row:first-child {
  border: 1px solid red;
}
.label-text {
  text-align: left;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: normal;
  line-height: 20px;
  color: #202124;
}
.v-menu__content {
  left: initial !important;
}
::v-deep {
  .draggable-checkbox .v-input--selection-controls {
    margin: 0 !important;
    padding: 0 !important;
  }
  .v-select__selections {
    flex-wrap: wrap !important;
  }
}
</style>
